/* Upsell complémentaire du panier (Vanille Désiré)

   Pile live : le tiroir est <aside id="vd-cart-drawer" class="vd-drawer">
   (snippets/vd-yc-cart-drawer.liquid), ouvert par la classe `is-open` posée par
   assets/vd-yc.js. Il n'y a pas de custom element cart-drawer ni de Section
   Rendering API : seul le corps [data-vd-cart-body] est ré-injecté, le pied
   (où vit ce bloc) reste en place.

   On charge à l'ouverture du tiroir, et on recharge quand le corps est
   ré-injecté (ajout / quantité / retrait) pendant qu'il est ouvert.

   Source : routes.product_recommendations_url + '.json?intent=complementary'
   sur le dernier produit ajouté (cart.items[0]) — alimenté par l'app gratuite
   Search & Discovery : sans configuration admin, rien ne remonte et le bloc
   reste masqué. */

if (!customElements.get('vd-cart-upsell')) {
  customElements.define(
    'vd-cart-upsell',
    class VdCartUpsell extends HTMLElement {
      connectedCallback() {
        this.itemsContainer = this.querySelector('[data-vd-upsell-items]');
        this.host = document.getElementById('vd-cart-drawer') || this.closest('.vd-cart-drawer');
        if (!this.itemsContainer || !this.host) return;

        this.reload = this.reload.bind(this);

        if (this.hostIsOpen()) this.load();

        this.classObserver = new MutationObserver(() => {
          if (this.hostIsOpen()) {
            if (!this.loadedForOpening) this.load();
          } else {
            this.loadedForOpening = false;
          }
        });
        this.classObserver.observe(this.host, { attributes: true, attributeFilter: ['class'] });

        // Le corps du tiroir est ré-injecté à chaque mutation du panier.
        const body = this.host.querySelector('[data-vd-cart-body]');
        if (body) {
          this.bodyObserver = new MutationObserver(this.reload);
          this.bodyObserver.observe(body, { childList: true });
        }
      }

      disconnectedCallback() {
        if (this.classObserver) this.classObserver.disconnect();
        if (this.bodyObserver) this.bodyObserver.disconnect();
        clearTimeout(this.reloadTimer);
      }

      hostIsOpen() {
        return this.host.classList.contains('is-open');
      }

      reload() {
        clearTimeout(this.reloadTimer);
        this.reloadTimer = setTimeout(() => {
          if (this.hostIsOpen()) this.load();
        }, 250);
      }

      formatMoney(cents) {
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
            style: 'currency',
            currency: currency,
          }).format(cents / 100);
        } catch (error) {
          return (cents / 100).toFixed(2).replace('.', ',') + ' €';
        }
      }

      async load() {
        this.loadedForOpening = true;
        try {
          const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {
            headers: { Accept: 'application/json' },
          });
          const cartData = await cartResponse.json();
          if (!cartData.items || cartData.items.length === 0) {
            this.hidden = true;
            return;
          }

          const inCart = new Set(cartData.items.map((item) => item.product_id));
          const lastProductId = cartData.items[0].product_id;

          const url = `${this.dataset.recommendationsUrl}.json?product_id=${lastProductId}&limit=${
            this.dataset.limit || 4
          }&intent=complementary`;
          const response = await fetch(url);
          if (!response.ok) {
            this.hidden = true;
            return;
          }
          const data = await response.json();
          const products = (data.products || [])
            .filter((product) => !inCart.has(product.id))
            .slice(0, 2);

          if (products.length === 0) {
            this.hidden = true;
            return;
          }

          this.renderProducts(products);
          this.hidden = this.itemsContainer.children.length === 0;
        } catch (error) {
          console.error('[vd-cart-upsell]', error);
          this.hidden = true;
        }
      }

      renderProducts(products) {
        this.itemsContainer.textContent = '';
        products.forEach((product) => {
          const variant = (product.variants || []).find((v) => v.available);
          if (!variant) return;

          const card = document.createElement('div');
          card.className = 'vd-cart-upsell__card';

          if (product.featured_image) {
            const image = document.createElement('img');
            const separator = product.featured_image.includes('?') ? '&' : '?';
            image.src = `${product.featured_image}${separator}width=160`;
            image.alt = product.title;
            image.width = 56;
            image.height = 56;
            image.loading = 'lazy';
            image.className = 'vd-cart-upsell__image';
            card.appendChild(image);
          }

          const meta = document.createElement('div');
          meta.className = 'vd-cart-upsell__meta';

          const title = document.createElement('a');
          title.className = 'vd-cart-upsell__title';
          title.href = `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${variant.id}`;
          title.textContent = product.title;
          meta.appendChild(title);

          if (variant.title && variant.title !== 'Default Title') {
            const format = document.createElement('span');
            format.className = 'vd-cart-upsell__price';
            format.textContent = variant.title;
            meta.appendChild(format);
          }

          const price = document.createElement('span');
          price.className = 'vd-cart-upsell__price';
          price.textContent = this.formatMoney(variant.price);
          meta.appendChild(price);

          card.appendChild(meta);

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'vd-cart-upsell__add';
          button.textContent = this.dataset.addLabel || window.vdI18n?.addToCart || '+';
          button.setAttribute('aria-label', `${this.dataset.addAria || button.textContent} : ${product.title}${variant.title && variant.title !== 'Default Title' ? ' · ' + variant.title : ''}`);
          button.addEventListener('click', () => this.addVariant(variant.id, button));
          card.appendChild(button);

          this.itemsContainer.appendChild(card);
        });
      }

      async addVariant(variantId, button) {
        button.disabled = true;
        try {
          const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });
          const parsed = await response.json().catch(() => null);
          if (!response.ok || (parsed && parsed.status)) {
            console.error('[vd-cart-upsell]', (parsed && (parsed.description || parsed.message)) || response.status);
            button.disabled = false;
            return;
          }

          if (window.vdCart) {
            await window.vdCart.refresh();
          } else {
            window.location.reload();
          }
        } catch (error) {
          console.error('[vd-cart-upsell]', error);
          button.disabled = false;
        }
      }
    }
  );
}
